/**
 * Audience
 * @module
 */
var _ = require('optimizely-client-fn');

var Condition = require('../lib/condition');
var KeyPath = require('../utils/key_path');
var Logger = require('../utils/logger');
var Visitor = require('../visitor');
/**
 * Creates an ImpressionTicket based on the VisitorProfile, audiences
 * @param {module:stores/visitor~VisitorProfile} visitorProfile
 * @param {Audience} audience
 * @param {Object.<AudienceMatcher>} matchersMap
 * @return {boolean|undefined}
 */
exports.isInAudience = function(visitorProfile:import('core/stores/visitor').VisitorProfile, audience:Audience, matchersMap:Record<string, AudienceMatcher>): boolean|undefined {
  var evaluateFn = createEvaluateFn(visitorProfile, matchersMap);
  Logger.groupCollapsed('Checking audience', audience.name, audience.id, audience);
  Logger.debug('Visitor Profile:', visitorProfile);
  var err;

  if (__INVARIANT__) {
    require('core/utils/invariant').isType('Conditions', audience.conditions);
  }

  try {
    var result = Condition.evaluate(audience.conditions, evaluateFn);
  } catch (e) {
    err = e;
    result = false;
  }

  Logger.groupEnd();
  if (err) {
    Logger.error('Audience / Error evaluating audience', getDescription(audience), ':', err);
  }
  Logger.log('Is ' + (result ? 'in' : 'NOT in') + ' audience:', getDescription(audience));
  return result;
};

/**
 * Recursively collect fieldsNeeded from matchers required by audience conditions
 * @param {Conditions} conditions
 * @param {Object.<AudienceMatcher>} matchersMap
 * @returns {Object.<KeyPath>} map (set) of required fields, from their
 *        serialized representation => field key path.
 */
exports.requiredAudienceFieldsForConditions = function requiredAudienceFieldsForConditions(conditions:Condition[], matchersMap:Record<string, AudienceMatcher>): Record<string, KeyPath> {
  var result = {} as any;
  _.each(conditions, function(condition) {
    if (_.isArray(condition)) {
      _.extend(result, requiredAudienceFieldsForConditions(condition, matchersMap));
    } else if (_.isObject(condition)) {
      var matcher = matchersMap[condition.type];
      if (matcher) {
        var fieldsNeeded = resolveFieldsNeeded(matcher.fieldsNeeded, condition);

        _.each(fieldsNeeded, function(fieldKey) {
          result[Visitor.serializeFieldKey(fieldKey)] = fieldKey;
        });
      }
    }
  });
  return result;
};

/**
 * @callback evaluateFn
 * @param {Condition} condition
 * @return {boolean|undefined}
 */

/**
 * Creates an evaluate function bound to the current visitor state
 * @param {module:stores/visitor~VisitorProfile} visitorProfile
 * @param {Object.<AudienceMatcher>} matchersMap
 * @return {evaluateFn}
 */
function createEvaluateFn(visitorProfile:import('core/stores/visitor').VisitorProfile, matchersMap:Record<string, AudienceMatcher>): evaluateFn {
  return function(condition) {
    var type = condition.type;
    var matcher = matchersMap[type];
    if (!matcher) {
      throw new Error('Audience / No matcher found for type=' + type);
    }

    if (matcher.fieldsNeeded) {
      var fieldsNeeded = resolveFieldsNeeded(matcher.fieldsNeeded, condition);

      for (var i = 0; i < fieldsNeeded.length; i++) {
        var fieldKey = fieldsNeeded[i];
        var value = KeyPath.getFieldValue(visitorProfile, fieldKey);
        if (_.isUndefined(value)) {
          // If some input is undefined (pending) then return undefined
          Logger.debug('Audience / Required field', fieldKey, 'for type', type, 'has no value');
          return;
        }
      }
    }

    Logger.debug('Matching condition:', condition, 'to values:', visitorProfile);
    var result = matcher.match(visitorProfile, condition);

    // If result is undefined, return undefined
    if (_.isUndefined(result)) {
      return;
    }
    // Cast result to boolean
    return !!result;
  };
}

/**
 * @param {FieldsNeeded} fieldsNeeded
 * @param {Condition} condition
 * @returns {Array.<KeyPath>}
 *        Key paths to fields required by the given matcher.
 */
function resolveFieldsNeeded(fieldsNeeded:FieldsNeeded, condition:Condition): KeyPath[] {
  var resolved = (typeof fieldsNeeded === 'function')
    ? fieldsNeeded(condition)
    : fieldsNeeded;

  // Backwards compatibility with old plugins, who provide a single bare string
  // rather than a key path.
  if (typeof resolved === 'string') {
    resolved = [resolved];
  }

  if (!_.isArray(resolved)) {
    Logger.warn('Couldn\'t determine fieldsNeeded for matcher; assuming []');
    return [];
  }

  return resolved;
}

function getDescription(audience) {
  return audience.name ? (audience.name + ' (' + audience.id + ')') : audience.id;
}

if (__TEST__) {
  exports.createEvaluateFn = createEvaluateFn;
  exports.resolveFieldsNeeded = resolveFieldsNeeded;
}
